<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { NotificationDocument } from "@kekkle/shared/types/documents/notification-document/notification-document";
import { NotificationType } from "@kekkle/shared/enums/notification-type";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { dateTimeAgo } from "@/helpers/dates/date-time-ago";
import KekklePulseDot from "@/components/ui/KekklePulseDot.vue";
import TinyUserThumb from "@/components/TinyUserThumb.vue";
import {
  ChatAlt2Icon,
  CameraIcon,
  VideoCameraIcon,
  CalendarIcon,
  UserAddIcon,
  SparklesIcon,
  SpeakerphoneIcon,
} from "@heroicons/vue/outline";

type Props = {
  notification: NotificationDocument;
};

type Emits = {
  (e: "click", notification: NotificationDocument): void;
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const groupsStore = useGroupsStore();

const groupName = computed<string>(() => {
  const liveGroup = groupsStore.userGroups.get(props.notification.group_id);
  if (liveGroup && liveGroup.name) {
    return liveGroup.name;
  }
  return props.notification.group_name || "Groep";
});

const actorName = computed<string>(() => {
  const liveGroup = groupsStore.userGroups.get(props.notification.group_id);
  const liveMember = liveGroup?.members?.[props.notification.actor_id];
  if (liveMember && liveMember.name) {
    return liveMember.name;
  }
  return props.notification.actor_name || "Iemand";
});

const formattedMessage = computed<string>(() => {
  const translationKey = `notifications.types.${props.notification.type}`;
  return t(translationKey, {
    actor: actorName.value,
    group: groupName.value,
  });
});

const formattedTime = computed<string>(() => {
  if (!props.notification.created_at) return "";
  return dateTimeAgo(props.notification.created_at);
});

const isCommentType = computed<boolean>(() => {
  const type = props.notification.type;
  return (
    type === NotificationType.QUOTE_COMMENT ||
    type === NotificationType.PHOTO_COMMENT ||
    type === NotificationType.EVENT_COMMENT ||
    type === NotificationType.DILEMMA_COMMENT ||
    type === NotificationType.SHORT_COMMENT
  );
});

const isPhotoType = computed<boolean>(() => {
  const type = props.notification.type;
  return (
    type === NotificationType.PHOTO_CREATE ||
    type === NotificationType.PHOTO_YEARLY_ANNIVERSARY
  );
});

const isShortType = computed<boolean>(() => {
  return props.notification.type === NotificationType.SHORT_CREATE;
});

const isEventType = computed<boolean>(() => {
  return props.notification.type === NotificationType.EVENT_CREATE;
});

const isMemberJoinType = computed<boolean>(() => {
  return props.notification.type === NotificationType.GROUP_MEMBER_JOINED;
});

const isSparkleType = computed<boolean>(() => {
  const type = props.notification.type;
  return (
    type === NotificationType.DILEMMA_CREATE ||
    type === NotificationType.QUOTE_YEARLY_ANNIVERSARY
  );
});

function handleClick() {
  emit("click", props.notification);
}
</script>

<template>
  <div
    class="cursor-pointer rounded-lg border-2 border-black-dark bg-white p-3 transition-all hover:brightness-105 dark:bg-kekkle-dark-light"
    :class="
      notification.is_read
        ? 'shadow-none'
        : 'shadow-yellow-hard-sm dark:shadow-purple-hard-sm'
    "
    @click="handleClick"
  >
    <!-- Top Row: Group Badge & Relative Time / Unread Pulse -->
    <div class="mb-1.5 flex items-center justify-between gap-2">
      <span
        translate="no"
        class="inline-flex items-center rounded-full border border-black-dark/10 bg-kekkle-yellow/30 px-2.5 py-0.5 text-xs font-semibold text-kekkle-dark dark:border-white/10 dark:bg-kekkle-yellow/20 dark:text-slate-200"
      >
        {{ groupName }}
      </span>
      <div class="flex items-center gap-2">
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ formattedTime }}
        </span>
        <KekklePulseDot
          v-if="!notification.is_read"
          color="blue"
          size="normal"
        />
      </div>
    </div>

    <!-- Notification Body -->
    <div class="flex items-start gap-3">
      <!-- Left: Actor Avatar / Icon -->
      <div class="relative shrink-0">
        <TinyUserThumb
          v-if="notification.actor_id"
          :uuid="notification.actor_id"
          :group-id="notification.group_id"
          class="pointer-events-none"
        />
        <div
          v-else
          class="flex h-10 w-10 items-center justify-center rounded-full border border-black-dark/10 bg-kekkle-yellow/20 text-kekkle-dark dark:bg-kekkle-yellow/10 dark:text-slate-200"
        >
          <SparklesIcon v-if="isSparkleType" class="h-5 w-5 text-yellow-500" />
          <SpeakerphoneIcon v-else class="h-5 w-5 text-kekkle-red" />
        </div>

        <!-- Small Type Icon Badge Overlay -->
        <span
          class="absolute -right-1 -bottom-1 flex h-4 w-4 items-center justify-center rounded-full border border-white bg-white shadow-sm dark:border-kekkle-dark dark:bg-kekkle-dark"
        >
          <ChatAlt2Icon v-if="isCommentType" class="h-3 w-3 text-blue-500" />
          <VideoCameraIcon
            v-else-if="isShortType"
            class="h-3 w-3 text-amber-500"
          />
          <CameraIcon v-else-if="isPhotoType" class="h-3 w-3 text-purple-500" />
          <CalendarIcon
            v-else-if="isEventType"
            class="h-3 w-3 text-green-500"
          />
          <UserAddIcon
            v-else-if="isMemberJoinType"
            class="h-3 w-3 text-orange-500"
          />
          <SparklesIcon
            v-else-if="isSparkleType"
            class="h-3 w-3 text-yellow-500"
          />
          <SpeakerphoneIcon v-else class="h-3 w-3 text-kekkle-red" />
        </span>
      </div>

      <!-- Right: Text & Snippet -->
      <div class="min-w-0 flex-1">
        <p class="text-sm leading-snug font-medium dark:text-slate-200">
          {{ formattedMessage }}
        </p>
        <p
          v-if="notification.snippet"
          class="mt-1 line-clamp-2 rounded-lg border border-gray-200 bg-gray-50 p-1.5 text-xs text-gray-600 italic dark:border-gray-700 dark:bg-kekkle-dark dark:text-gray-300"
        >
          "{{ notification.snippet }}"
        </p>
      </div>
    </div>
  </div>
</template>
